import React from 'react';

type InterviewSetupProps = {
  micPermission: boolean;
  setMicPermission: (granted: boolean) => void;
};

export const InterviewSetup: React.FC<InterviewSetupProps> = ({
  micPermission,
  setMicPermission
}) => {
  const checkMicrophonePermission = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        globalThis.alert(
          'Your browser does not support camera or microphone access. Please use a modern browser like Chrome, Firefox, or Edge.'
        );
        setMicPermission(false);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      stream.getTracks().forEach(track => track.stop());
      setMicPermission(true);
    } catch (error) {
      globalThis.alert(
        'Microphone permission is required to start the test. Please allow microphone access in your browser settings.'
      );
      setMicPermission(false);
      throw error;
    }
  };

  return (
    <div>
      {/* Content */}
      <div className='mb-10 grid grid-cols-1 gap-6 md:grid-cols-2'>
        {/* Left Card */}
        <div className='rounded-xl bg-gray-50 p-6'>
          <h3 className='mb-4 font-semibold text-gray-900'>
            What You’ll Experience
          </h3>

          <ul className='space-y-3 text-sm text-gray-700'>
            <li className='flex items-start gap-2'>
              <span>🎥</span>
              <span>
                <strong>Face-to-Face Interview</strong> — Realistic AI
                interviewer experience
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <span>🎤</span>
              <span>
                <strong>Speak Your Answers</strong> — Practice verbal
                communication
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <span>📝</span>
              <span>
                <strong>Live Transcription</strong> — Speech converts to text
                instantly
              </span>
            </li>
            <li className='flex items-start gap-2'>
              <span>📊</span>
              <span>
                <strong>Detailed Feedback</strong> — Body language, tone, and
                skill analysis
              </span>
            </li>
          </ul>
        </div>

        {/* Right Card */}
        <div className='rounded-xl bg-gray-50 p-6'>
          <h3 className='mb-4 font-semibold text-gray-900'>
            What You’ll Experience
          </h3>

          <ul className='space-y-3 text-sm'>
            {[
              'Recording only during the interview',
              'You control start and stop',
              'Only you can access recordings',
              'Delete anytime, instantly',
              'Never shared without permission',
              'Encrypted storage (bank-level)'
            ].map(item => (
              <li
                key={item}
                className='flex items-center gap-2 rounded-lg bg-emerald-100 px-4 py-2 text-emerald-800'
              >
                <span className='font-bold'>✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Footer Button */}
      <div className='flex justify-center'>
        <button
          onClick={checkMicrophonePermission}
          className={`rounded-xl px-6 py-3 font-medium text-white shadow-md ${micPermission ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {micPermission ? 'Microphone Allowed' : 'Allow Camera & Microphone'}
        </button>
      </div>
    </div>
  );
};
